// Правила живой проверки (registry.lint): замечания в тексте (inlineLint - с подчёркиванием) и по документу
// в целом - ссылки на объекты, названия, источники, состав работы по табл. 1 СТО, данные титула, служебные
// элементы. Полная проверка - «Проверить по СТО» (sto_check по собранному DOCX).
import type { RegistryApi, LintIssue, Ctx } from '../../core/registry';
import type { DocIndex, IndexEntry } from '../../core/docIndex';
import type { Node as PMNode } from '@tiptap/pm/model';
import { focusInside } from '../../core/editor/insert';
import { gotoNode } from '../../core/docIndex';
import { scanDoc, applyFix } from './inlineLint';
import { ELEMENTS, elementNeed, wt, richText, NB, isSheet } from './data';
import { findStruct, insertStructElement } from './structure';
import { sourceProblems } from './sources';
import { fillKeywords } from './keywords';
import { suggestName, prevBlock, refParts } from './autoname';

const OBJ_WORD: Record<string, [string, string]> = {
  figure: ['рисунок', 'рисунок'], stoTable: ['таблицу', 'таблица'], listing: ['листинг', 'листинг'], appendix: ['приложение', 'приложение'],
};

/** id объектов -> позиция первой перекрёстной ссылки (4.5.4, 4.9.3, 6.1.5, 4.16.2). */
/*  Диапазон «на рисунках [ссылка]–[ссылка]» (между ссылками только тире/дефис) упоминает и все объекты того же
 *  вида между концами: иначе на рисунок 10 из «9–11» предлагалось дописать «(рисунок 10)». */
export function firstRefs(doc: PMNode, entries: IndexEntry[] = []): Map<string, number> {
  const first = new Map<string, number>();
  const ranges: [string, string, number][] = [];
  doc.descendants((n, pos) => {
    if (n.type.name === 'xref' && n.attrs.target && !first.has(n.attrs.target)) first.set(n.attrs.target, pos);
    if (n.isTextblock) {
      let prev: { target: string; pos: number } | null = null;
      let gap = '';
      n.forEach((c, off) => {
        if (c.type.name === 'xref' && c.attrs.target) {
          if (prev && /^\s*[-–—]\s*$/.test(gap)) ranges.push([prev.target, c.attrs.target, prev.pos]);
          prev = { target: c.attrs.target, pos: pos + 1 + off };
          gap = '';
        } else if (c.isText) {
          gap += c.text || '';
        } else {
          prev = null;
        }
      });
    }
    return true;
  });
  for (const [a, z, p] of ranges) {
    const ia = entries.findIndex((e) => e.id === a);
    const iz = entries.findIndex((e) => e.id === z);
    if (ia < 0 || iz <= ia || entries[ia].type !== entries[iz].type) continue;
    for (let i = ia + 1; i < iz; i++) {
      const e = entries[i];
      if (e.type === entries[ia].type && (!first.has(e.id) || (first.get(e.id) as number) > p)) first.set(e.id, p);
    }
  }
  return first;
}

/** Абзац, в который можно дописать «(рисунок N)» (как stoweb/autorefs._para_ok): текст, не подпись и не метка
 *  «Цель работы:», хотя бы одно слово от 3 букв; без знака в конце - предложение от 8 слов. */
function paraUsable(n: PMNode): boolean {
  const t = n.textContent.trim();
  if (!t || /^\s*(?:Рисунок|Рис\.|Таблица|Табл\.|Листинг|Продолжение|Окончание|Цель|Тема|Вариант|Ход\s+работы|Порядок\s+выполнения)(?![а-яё])/i.test(t)
    || !/[А-Яа-яЁёA-Za-z]{3}/.test(t)) return false;
  if (/[.:;!?…)»"]$/.test(t)) return true;
  return t.split(/\s+/).length >= 8 && /[A-Za-zА-Яа-яЁё]$/.test(t);
}

/** Абзац прямо перед объектом (пустые пропускаются; перед объектом перечисление - его последняя позиция), в который
 *  можно дописать ссылку, - id; иначе null (тогда - новый абзац перед объектом). */
function prevParagraph(ix: DocIndex, e: IndexEntry): string | null {
  const pb = prevBlock(ix.doc, e.pos);
  if (!pb) return null;
  let n: PMNode | null = pb.node;
  while (n && n.type.name === 'stoList' && n.childCount) {
    const item: PMNode = n.child(n.childCount - 1);
    let last: PMNode | null = null;
    item.forEach((c) => { if (c.type.name === 'paragraph' || c.type.name === 'stoList') last = c; });
    n = last;
  }
  return n && n.type.name === 'paragraph' && n.attrs.id && paraUsable(n) ? n.attrs.id : null;
}

/** Новый абзац перед объектом: «Схема стенда представлена на рисунке N.» / «Результат представлен на рисунке N.». */
export function insertRefParagraph(ctx: Ctx, objId: string, type: string) {
  const ed = ctx.editor;
  if (!ed) return;
  let at = -1;
  let cap = '';
  ed.state.doc.descendants((n, pos) => {
    if (at >= 0) return false;
    if (n.attrs?.id === objId) { at = pos; n.forEach((c) => { if (c.type.name === 'caption') cap = c.textContent; }); return false; }
    return true;
  });
  if (at < 0) return;
  const [lead, tail] = refParts(type, cap);
  ed.chain().insertContentAt(at, { type: 'paragraph', content: [{ type: 'text', text: lead },
    { type: 'xref', attrs: { target: objId } }, { type: 'text', text: tail }] }).run();
}

/** Вписать название в пустую подпись объекта (подпись таблицы может отсутствовать - создаётся). */
export function setCaptionText(ctx: Ctx, objId: string, name: string) {
  const ed = ctx.editor;
  if (!ed) return;
  const { state } = ed;
  let done = false;
  state.doc.descendants((n, pos) => {
    if (done) return false;
    if (n.attrs?.id !== objId) return true;
    done = true;
    const first = n.childCount ? n.child(0) : null;
    const tr = state.tr;
    if (first && first.type.name === 'caption') {
      if (first.textContent.trim()) return false;
      tr.insertText(name, pos + 2, pos + 2 + first.content.size);
    } else {
      tr.insert(pos + 1, state.schema.nodes.caption.create(null, state.schema.text(name)));
    }
    ed.view.dispatch(tr);
    return false;
  });
}

/** Дописать «(рисунок N)» в конец абзаца перед объектом - перед завершающим знаком. Если абзац уже кончается
 *  словом объекта («…сведены в таблицу», «…показан на рисунке:») - дописывается только номер. */
export function addRefToParagraph(ctx: Ctx, paraId: string, targetId: string, word: string) {
  const ed = ctx.editor;
  if (!ed) return;
  ed.state.doc.descendants((n, pos) => {
    if (n.attrs?.id !== paraId) return true;
    const text = n.textContent;
    const m = text.match(/[.:;!?…]?[\s\u00a0]*$/);
    const end = pos + 1 + n.content.size;
    const tail = m ? m[0] : '';
    const body = text.slice(0, text.length - tail.length);
    const stem = word.slice(0, 5).toLowerCase();                 // «рисун», «табли», «листи», «прило»
    // листинг - всегда в скобках в конце предложения (6.1.5): не «…в листинге 3»
    const hanging = word !== 'листинг' && new RegExp('(?:^|[\\s(\\u00a0])' + stem + '[а-яё]*$', 'i').test(body);
    const at = end - tail.length;
    ed.chain().insertContentAt(at, hanging
      ? [{ type: 'text', text: NB }, { type: 'xref', attrs: { target: targetId } }]
      : [{ type: 'text', text: ` (${word}${NB}` }, { type: 'xref', attrs: { target: targetId } }, { type: 'text', text: ')' }]).run();
    return false;
  });
}

/** Принять дописанное при загрузке в блоке (абзац, подпись объекта id или блок в позиции pos): метка auto у текста
 *  снимается, у ссылок xref.auto -> false, у записей ссылки на источник - items[].auto (позиции не сдвигаются). */
export function acceptAuto(ctx: Ctx, id: string | null, pos: number) {
  const ed = ctx.editor;
  if (!ed) return;
  const { state } = ed;
  let at = -1;
  if (id) {
    state.doc.descendants((n, p) => {
      if (at >= 0) return false;
      if (n.attrs?.id !== id) return true;
      if (n.isTextblock) at = p;
      else if (n.childCount && n.child(0).type.name === 'caption') at = p + 1;
      return false;
    });
  }
  if (at < 0) at = pos;
  const block = state.doc.nodeAt(at);
  if (!block || !block.isTextblock) return;
  const tr = state.tr;
  const mark = state.schema.marks.auto;
  if (mark) tr.removeMark(at + 1, at + block.nodeSize - 1, mark);
  block.forEach((c, off) => {
    if (c.type.name === 'xref' && c.attrs.auto) tr.setNodeMarkup(at + 1 + off, undefined, { ...c.attrs, auto: false });
    if (citeAuto(c)) tr.setNodeMarkup(at + 1 + off, undefined, { ...c.attrs, items: (c.attrs.items || []).map(({ auto: _a, ...it }: any) => it) });
  });
  if (tr.docChanged) ed.view.dispatch(tr);
}

/** Ссылка на источник с записями, дописанными при загрузке (items[].auto - stoweb/autorefs.ensure_cites). */
const citeAuto = (c: PMNode) => c.type.name === 'cite' && (c.attrs.items || []).some((it: any) => it.auto);

/** Блок с дописанным при загрузке (метка auto у текста, xref.auto, cite items[].auto)? all - весь текст дописан
 *  (новый абзац); cite - дописана ссылка на источник. */
function autoInfo(n: PMNode): { any: boolean; all: boolean; target: string; cite: boolean } {
  let txt = 0; let auto = 0; let any = false; let target = ''; let cite = false;
  n.forEach((c) => {
    const a = c.isText ? c.marks.some((m) => m.type.name === 'auto') : (c.type.name === 'xref' && !!c.attrs.auto) || citeAuto(c);
    if (a) any = true;
    if (c.type.name === 'xref' && c.attrs.auto && !target) target = c.attrs.target;
    if (citeAuto(c)) cite = true;
    if (c.isText && (c.text || '').trim()) { txt++; if (a) auto++; }
  });
  return { any, all: txt > 0 && auto === txt, target, cite };
}

function openMeta(tab: string, field?: string) {
  return (c: Ctx) => { void c.openDialog('sto.meta', { tab, field }); };
}

export function registerLint(r: RegistryApi) {
  // --- замечания в тексте (подчёркнутые)
  r.lint.add({ id: 'sto.inline', title: 'В тексте', order: 5, run: (ix) => scanDoc(ix.doc, ix.numbering).map((x) => ({
    sev: x.sev, msg: x.msg, node: x.node, rule: x.rule, code: x.code,
    fix: x.fixes[0] ? { label: x.fixes[0].label, run: (c: Ctx) => { if (c.editor && !applyFix(c.editor, x)) c.toast('Уже исправлено', 'info'); } } : undefined,
  })) });

  // --- ссылки на объекты
  r.lint.add({ id: 'objects.refs', title: 'Ссылки на объекты', order: 10, run: (ix: DocIndex) => {
    const refs = firstRefs(ix.doc, ix.entries);
    const out: LintIssue[] = [];
    for (const e of ix.entries) {
      if (!OBJ_WORD[e.type] || !e.num) continue;
      const p = refs.get(e.id);
      const [acc, nom] = OBJ_WORD[e.type];
      if (p === undefined) {
        const para = e.type === 'appendix' ? null : prevParagraph(ix, e);
        let cap = '';
        e.node.forEach((c) => { if (c.type.name === 'caption') cap = c.textContent; });
        // рисунок и таблица без ссылки - ошибка и в полной проверке (FIG-REF-MISSING, TBL-REF-MISSING); исправление -
        // «(рисунок N)» в абзац прямо перед объектом, иначе (после заголовка, другого объекта) - новый абзац перед ним
        out.push({ sev: e.type === 'figure' || e.type === 'stoTable' ? 'E' : 'W', code: e.type === 'figure' ? 'FIG-REF-MISSING' : e.type === 'stoTable' ? 'TBL-REF-MISSING' : 'REF-MISSING',
          node: e.id, rule: e.type === 'appendix' ? '4.16.2' : e.type === 'listing' ? '6.1' : e.type === 'figure' ? '4.5.4' : '4.9.3',
          msg: `На ${acc} ${e.num.num} нет ссылки в тексте - наберите «@» в абзаце перед ним`,
          fix: para ? { label: `Дописать «(${nom} ${e.num.num})» в предыдущий абзац`, run: (c) => addRefToParagraph(c, para, e.id, nom) }
            : e.type !== 'appendix' ? { label: `Вставить абзац «${refParts(e.type, cap).join(e.num.num)}»`, run: (c) => insertRefParagraph(c, e.id, e.type) }
              : undefined });
      } else if (p > e.pos && e.type !== 'appendix') {
        out.push({ sev: 'W', node: e.id, rule: e.type === 'figure' ? '4.5.4' : '4.9.3', msg: `Первая ссылка на ${acc} ${e.num.num} стоит после него - объект помещают после первой ссылки` });
      }
    }
    return out;
  } });

  // --- дописанное сайтом при загрузке .docx (stoweb/autorefs.py, autonames.py): ссылки (xref.auto), текст с меткой
  //     auto - «(рисунок 3)», абзац «Результат представлен на рисунке 3.», название объекта. Одно замечание на блок;
  //     code - «принять все однотипные» (метки снимаются без сдвига позиций)
  r.lint.add({ id: 'refs.auto', title: 'Добавлено при загрузке', order: 12, run: (ix: DocIndex) => {
    const out: LintIssue[] = [];
    ix.doc.descendants((n, pos, parent) => {
      if (!n.isTextblock) return true;
      const a = autoInfo(n);
      if (!a.any) return false;
      const isCap = n.type.name === 'caption';
      const id: string | null = (isCap ? parent?.attrs?.id : n.attrs?.id) || parent?.attrs?.id || null;
      const own = id ? ix.byId.get(id) : undefined;
      const te = a.target ? ix.byId.get(a.target) : undefined;
      const what = te?.num?.label ? te.num.label.replace(NB, ' ') : 'объект';
      const text = n.textContent.trim();
      const cut = (s: string) => (s.length > 70 ? s.slice(0, 70) + '…' : s);
      let msg: string;
      let rule = te?.type === 'appendix' ? '4.16.2' : te?.type === 'stoTable' ? '4.9.3' : te?.type === 'listing' ? '6.1.5' : '4.5.4';
      if (isCap) {
        rule = own?.type === 'stoTable' ? '4.9.4' : own?.type === 'listing' ? '6.1.5' : '4.5.7';
        msg = `${(own?.num?.label || 'Объект').replace(NB, ' ')}: название «${cut(text)}» подобрано автоматически по тексту (в исходнике его не было) - проверьте`;
      } else if (parent?.type.name === 'tableHeader' || parent?.type.name === 'tableCell') {
        // графа «№» с номерами опытов переименована (stoweb/autonames.npp_headers)
        rule = '4.9.13';
        msg = `Графа «№» переименована в «${cut(text)}» автоматически (графу «№ п/п» в таблицу не включают, номера опытов - данные) - проверьте название графы`;
      } else if (a.cite && !a.target) {
        rule = '4.14.10';
        msg = 'Ссылка на источник дописана автоматически (на запись списка не было ссылки в тексте) - проверьте, что сведения источника использованы в этом абзаце';
      } else if (a.all) {
        msg = `Абзац «${cut(text)}» добавлен автоматически: в исходнике перед «${what}» не было ссылки на него - проверьте формулировку`;
      } else {
        msg = `Ссылка на «${what}» дописана автоматически (в исходнике её не было) - проверьте формулировку`;
      }
      out.push({ sev: 'I', code: 'AUTO', node: id, rule, msg, fix: { label: 'Принять', run: (c: Ctx) => acceptAuto(c, id, pos) } });
      return false;
    });
    return out;
  } });

  // название объекта - обязательно (4.5.7, 4.9.4, 6.1.5): как в полной проверке - ошибка; исправление - название по
  // тексту (предложение со ссылкой, строка над объектом, головка таблицы, заголовок раздела), иначе курсор в подпись
  r.lint.add({ id: 'captions', title: 'Названия объектов', order: 20, run: (ix) => ix.entries
    .filter((e) => ['figure', 'stoTable', 'listing'].includes(e.type) && !captionText(e.node))
    .map((e) => {
      const name = suggestName(ix, e);
      return { sev: 'E' as const, node: e.id, rule: e.type === 'stoTable' ? '4.9.4' : e.type === 'listing' ? '6.1.5' : '4.5.7',
        code: e.type === 'stoTable' ? 'TBL-CAPTION-FMT' : e.type === 'listing' ? 'LISTING-CAPTION-FMT' : 'FIG-CAPTION-FMT',
        msg: `${(e.num?.label || 'Объект').replace(NB, ' ')}: нет названия - впишите его`,
        fix: name ? { label: `Подставить «${name.length > 50 ? name.slice(0, 50) + '…' : name}»`, run: (c: Ctx) => setCaptionText(c, e.id, name) }
          : { label: 'Ввести название', run: (c: Ctx) => { if (c.editor) focusInside(c.editor, e.pos + 1); } } };
    }) });

  // --- источники
  r.lint.add({ id: 'sources', title: 'Источники', order: 30, run: (ix) => {
    const cited = new Set<string>();
    // ссылки [N] и в тексте сносок (4.14.6: «см. [3]» в подстрочной ссылке) - тоже ссылки на источник
    const walkJson = (j: any) => { if (!j) return; if (j.type === 'cite') for (const it of j.attrs?.items || []) cited.add(it.source); (j.content || []).forEach(walkJson); };
    ix.doc.descendants((n) => {
      if (n.type.name === 'cite') for (const it of n.attrs.items || []) cited.add(it.source);
      if (n.type.name === 'footnote') (n.attrs.content || []).forEach(walkJson);
      return true;
    });
    const out: LintIssue[] = [];
    const openSrc = (id: string) => (c: Ctx) => { void c.openDialog('sto.source', { id }); };
    for (const s of ix.sources) {
      const n = ix.numbering.sources.get(s.id) ?? '';
      if (!cited.has(s.id)) out.push({ sev: 'E', code: 'SRC-UNCITED', node: 'src:' + s.id, rule: '4.14.10',
        msg: `На источник ${n} нет ссылки в тексте: ${(s.formatted || s.text || s.title || '').slice(0, 60)}`,
        fix: { label: 'Сослаться здесь', run: (c) => c.editor?.chain().focus().insertContent({ type: 'cite', attrs: { items: [{ source: s.id, loc: '' }] } }).run() } });
      for (const p of sourceProblems.get(s.id) || []) out.push({ sev: 'W', node: 'src:' + s.id, rule: '4.13', msg: `Источник ${n}: ${p}`, fix: { label: 'Дополнить', run: openSrc(s.id) } });
    }
    return out;
  } });

  // --- состав работы по табл. 1 СТО и порядок элементов
  r.lint.add({ id: 'structure', title: 'Структура', order: 40, run: (ix) => {
    const out: LintIssue[] = [];
    const m = ix.meta;
    if (!m || isSheet(m)) return out;                 // чистый лист: структуры работы нет - только текст
    const type = m.type || 'lab';
    const w = wt(type);
    const has = {
      zadanie: m.zadanie?.enabled, remarks: m.remarks_page, referat: m.referat?.enabled || m.annotation?.enabled, toc: true,
      intro: !!findStruct(ix.doc, ['intro']), concl: !!findStruct(ix.doc, ['conclusions', 'conclusion']),
      sources: !!findStruct(ix.doc, ['sources']) || ix.sources.length > 0, ai: m.ai_usage?.enabled,
    } as Record<string, boolean>;
    for (const el of ELEMENTS) {
      const need = elementNeed(el, type);
      if (!need || has[el.id]) continue;
      const fix = el.id === 'intro' ? { label: 'Добавить «Введение»', run: (c: Ctx) => c.editor && insertStructElement(c.editor, 'intro') }
        : el.id === 'concl' ? { label: `Добавить «${w.concl === 'conclusions' ? 'Выводы' : 'Заключение'}»`, run: (c: Ctx) => c.editor && insertStructElement(c.editor, w.concl) }
          : el.id === 'sources' ? { label: 'Добавить список источников', run: (c: Ctx) => c.editor && insertStructElement(c.editor, 'sources') }
            : el.id === 'zadanie' || el.id === 'remarks' || el.id === 'referat'
              ? { label: 'Включить', run: (c: Ctx) => c.store.getState().setMeta((mm) => el.id === 'remarks' ? { ...mm, remarks_page: true }
                : el.id === 'zadanie' ? { ...mm, zadanie: { ...mm.zadanie, enabled: true } } : { ...mm, referat: { ...mm.referat, enabled: true } }) }
              : undefined;
      out.push({ sev: need === '+' ? 'W' : 'I', node: el.id === 'zadanie' ? 'meta_zadanie' : el.id === 'referat' ? 'meta_referat' : null, rule: '2.2 (табл. 1)',
        msg: `${need === '+' ? 'Обязательный' : 'Рекомендуемый'} для вида «${w.name}» элемент: «${el.title}»`, fix });
    }
    // порядок: Введение - до разделов, Выводы/Заключение - после разделов
    let firstSec = -1; let lastSec = -1;
    ix.doc.forEach((n, off) => { if (n.type.name === 'heading' && Number(n.attrs.level) === 1 && n.textContent.trim()) { if (firstSec < 0) firstSec = off; lastSec = off; } });
    const intro = findStruct(ix.doc, ['intro']);
    if (intro && firstSec >= 0 && intro.pos > firstSec) out.push({ sev: 'W', node: intro.node.attrs.id, rule: '3.6', msg: '«Введение» должно стоять перед основной частью' });
    const concl = findStruct(ix.doc, ['conclusions', 'conclusion']);
    if (concl && lastSec > concl.pos) out.push({ sev: 'W', node: concl.node.attrs.id, rule: '3.8', msg: `«${concl.node.attrs.kind === 'conclusions' ? 'Выводы' : 'Заключение'}» должно идти после последнего раздела` });
    if (concl && concl.node.attrs.kind !== w.concl) out.push({ sev: 'I', node: concl.node.attrs.id, rule: '3.8',
      msg: `Для вида «${w.name}» обычно пишут «${w.concl === 'conclusions' ? 'Выводы' : 'Заключение'}»`,
      fix: { label: `Заменить на «${w.concl === 'conclusions' ? 'Выводы' : 'Заключение'}»`, run: (c) => c.editor?.view.dispatch(c.editor.state.tr.setNodeMarkup(concl.pos, undefined, { ...concl.node.attrs, kind: w.concl })) } });
    if (firstSec < 0 && !['essay', 'referat'].includes(type)) out.push({ sev: 'W', node: null, rule: '3.7', msg: 'Нет ни одного раздела основной части (заголовок 1-го уровня, Ctrl+Alt+1)' });
    return out;
  } });

  // --- данные титула и служебные элементы
  r.lint.add({ id: 'meta', title: 'Данные работы', order: 50, run: (ix) => {
    const out: LintIssue[] = [];
    const m = ix.meta;
    if (!m || isSheet(m)) return out;                 // чистый лист: без титула и данных работы
    if (!m.title?.trim()) out.push({ sev: 'W', node: 'meta_title', rule: '3.1', msg: 'Не задана тема работы (титульный лист)', fix: { label: 'Указать', run: openMeta('work', 'title') } });
    if (!m.discipline?.trim() && m.type !== 'practice') out.push({ sev: 'W', node: 'meta_title', rule: '3.1', msg: 'Не задана дисциплина (титульный лист)', fix: { label: 'Указать', run: openMeta('work', 'discipline') } });
    if (!m.teacher?.name?.trim()) out.push({ sev: 'W', node: 'meta_title', rule: '3.1', msg: 'Не указан руководитель (титульный лист)', fix: { label: 'Указать', run: openMeta('work', 'teacher') } });
    // обучающийся и соавторы (титул, реферат - 3.2.3, свойство «Автор» - 3.12.2; как writer/builder.coauthors)
    const st = m.student || ({} as any);
    if (!String(st.student || '').trim()) out.push({ sev: 'W', code: 'META-AUTHOR', node: 'meta_title', rule: '3.12.2',
      msg: 'Не указано ФИО обучающегося (титульный лист, реферат, свойство файла «Автор»)', fix: { label: 'Указать', run: openMeta('student', 'student') } });
    const mainFam = (String(st.student || '').trim().split(/\s+/)[0] || '').replace(/ё/g, 'е');
    const coRaw = String(st.coauthors || '').split(/[,;\n]+/).map((x) => x.replace(/\s+/g, ' ').trim()).filter(Boolean);
    const coDup = coRaw.filter((x) => mainFam && x.split(' ')[0].replace(/ё/g, 'е') === mainFam);
    if (coDup.length) out.push({ sev: 'W', node: 'meta_title', rule: '3.1', msg: `Соавтор совпадает с обучающимся («${coDup[0]}») - в «Соавторы» впишите только других участников группы`,
      fix: { label: 'Исправить', run: openMeta('student', 'coauthors') } });
    const coShort = coRaw.filter((x) => !coDup.includes(x) && !/^[А-ЯЁA-Z][а-яёa-z-]+\s+[А-ЯЁA-Z][а-яёa-z-]+\s+[А-ЯЁA-Z][а-яёa-z-]+$/.test(x));
    if (coShort.length) out.push({ sev: 'I', node: 'meta_title', rule: '3.1', msg: `Соавторы на титуле пишутся полностью (Фамилия Имя Отчество): ${coShort.slice(0, 3).map((x) => `«${x}»`).join(', ')}`,
      fix: { label: 'Изменить', run: openMeta('student', 'coauthors') } });
    const kw = m.keywords || [];
    // как в полной проверке: без ключевых слов - META-KEYWORDS (3.12.2), число вне 5–15 при реферате - замечание (3.2.6).
    // Пустой список при экспорте подбирается по тексту (stoweb/keywords.py) - замечание, а не ошибка; «Подобрать»
    // делает то же сейчас, чтобы автор увидел и проверил слова
    if (!kw.length) out.push({ sev: 'W', code: 'META-KEYWORDS', node: 'meta_title', rule: '3.12.2',
      msg: 'Нет ключевых слов (свойства DOCX/PDF, 3.12.2; в реферате - 5–15 слов, 3.2.6) - при экспорте они будут подобраны по тексту; подберите и проверьте сейчас',
      fix: { label: 'Подобрать', run: (c) => { void fillKeywords(c); } } });
    else if (kw.length < 5 || kw.length > 15) out.push({ sev: m.referat?.enabled ? 'W' : 'I', code: 'META-KEYWORDS', node: 'meta_title', rule: '3.2.6',
      msg: `Ключевых слов ${kw.length}, нужно 5–15 (реферат и свойства PDF)`, fix: { label: 'Изменить', run: openMeta('work', 'keywords') } });
    if (kw.length && m.keywords_auto) out.push({ sev: 'I', code: 'META-KEYWORDS-AUTO', node: 'meta_title', rule: '3.2.6',
      msg: `Ключевые слова подобраны автоматически по тексту работы (${kw.slice(0, 3).join(', ')}${kw.length > 3 ? '…' : ''}) - проверьте их`,
      fix: { label: 'Проверить', run: openMeta('work', 'keywords') } });
    // поля, заполненные сайтом при загрузке .docx (meta.auto, stoweb/docx_import): тема не с титула (из текста,
    // заголовка, имени файла), ФИО из профиля - проверить; «Проверено» или правка поля снимает отметку
    for (const k of (m.auto || []).filter((x) => x === 'title' || x === 'student' || x === 'teacher')) {
      if (k === 'title' && !m.title?.trim()) continue;
      // teacher - ФИО руководителя с титула (опечатка, инициалы) сверено со списком руководителей профиля
      const what = k === 'title' ? `тема работы взята не с титула («${m.title.slice(0, 60)}»)`
        : k === 'teacher' ? `ФИО руководителя взято из списка руководителей профиля (${m.teacher?.name || ''}) - на титуле оно с опечаткой или инициалами`
          : `ФИО обучающегося взято из профиля (${m.student?.student || ''})`;
      out.push({ sev: 'I', code: 'META-AUTO', node: 'meta_title', rule: '3.12.2', msg: `Заполнено сайтом при загрузке: ${what} - проверьте`,
        fix: { label: 'Проверено', run: (c) => c.store.getState().setMeta((mm) => ({ ...mm, auto: (mm.auto || []).filter((x) => x !== k),
          ...(k === 'title' ? { title_auto: false } : {}) })) } });
    }
    if (m.zadanie?.enabled && !richText(m.zadanie.doc).trim()) out.push({ sev: 'W', node: 'meta_zadanie', rule: '3.1', msg: 'Задание включено, но пустое', fix: { label: 'Заполнить', run: () => scrollSheet('zadanie') } });
    if (m.referat?.enabled && !richText(m.referat.doc).trim()) out.push({ sev: 'W', node: 'meta_referat', rule: '3.2', msg: 'Реферат включён, но текст не написан', fix: { label: 'Написать', run: () => scrollSheet('referat') } });
    if (m.annotation?.enabled && !richText(m.annotation.doc).trim()) out.push({ sev: 'W', node: 'meta_annotation', rule: '3.2', msg: 'Аннотация включена, но пустая', fix: { label: 'Написать', run: () => scrollSheet('referat') } });
    if (m.ai_usage?.enabled && !richText(m.ai_usage.facts).trim()) out.push({ sev: 'W', node: 'meta_ai', rule: '3.9', msg: 'Раздел об ИИ включён, но фактологическое описание пустое', fix: { label: 'Заполнить', run: openMeta('ai') } });
    if (m.type === 'practice') for (const [k, t] of [['kind', 'вид'], ['type', 'тип'], ['place', 'место']] as const) {
      if (!(m.practice as any)?.[k]) out.push({ sev: 'W', node: 'meta_title', rule: '3.1', msg: `Отчёт о практике: не указан ${t} практики`, fix: { label: 'Указать', run: openMeta('practice') } });
    }
    return out;
  } });

  r.lint.add({ id: 'empty', title: 'Пустые абзацы', order: 60, run: (ix) => {
    const out: LintIssue[] = [];
    let run = 0;
    ix.doc.forEach((n) => {
      if (n.type.name === 'paragraph' && !n.content.size) { run++; if (run === 2) out.push({ sev: 'I', node: n.attrs.id, rule: '4.1', msg: 'Несколько пустых абзацев подряд - отступы делает оформление, при экспорте они удаляются' }); } else run = 0;
    });
    return out;
  } });
}

export function scrollSheet(id: string) {
  const el = document.querySelector(`[data-sheet="${id}"]`) as HTMLElement | null;
  if (el) {
    el.scrollIntoView({ block: 'start', behavior: 'smooth' }); el.classList.add('sto-flash'); setTimeout(() => el.classList.remove('sto-flash'), 1500);
    // «Заполнить»/«Написать» - сразу курсор в поле листа (в конец текста)
    const pm = el.querySelector('.mini-editor') as HTMLElement | null;
    if (pm) setTimeout(() => {
      pm.focus();
      const sel = window.getSelection();
      if (sel) { const r = document.createRange(); r.selectNodeContents(pm); r.collapse(false); sel.removeAllRanges(); sel.addRange(r); }
    }, 60);
  }
}

function captionText(n: PMNode): string {
  let t = '';
  n.forEach((c) => { if (c.type.name === 'caption') t = c.textContent.trim(); });
  return t;
}

/** Перейти к узлу или псевдоузлу (meta_* - листы/диалог, src:<id> - источник). */
export function goTo(ctx: Ctx, node: string | null) {
  if (!node) return;
  if (node.startsWith('src:')) { void ctx.openDialog('sto.source', { id: node.slice(4) }); return; }
  if (node.startsWith('meta_')) {
    const sheet = { meta_title: 'title', meta_zadanie: 'zadanie', meta_remarks: 'remarks', meta_referat: 'referat', meta_annotation: 'referat', meta_toc: 'toc' }[node];
    if (sheet && document.querySelector(`[data-sheet="${sheet}"]`)) { scrollSheet(sheet); return; }
    if (node === 'meta_ai' || node === 'meta_sources') {
      const el = document.querySelector(node === 'meta_ai' ? '.sto-ai-widget' : '.sto-auto-sources') as HTMLElement | null;
      if (el) { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); el.classList.add('sto-flash'); setTimeout(() => el.classList.remove('sto-flash'), 1500); return; }
    }
    void ctx.openDialog('sto.meta', { tab: node === 'meta_ai' ? 'ai' : node === 'meta_title' ? 'work' : 'front' });
    return;
  }
  if (ctx.editor && !gotoNode(ctx.editor, node)) ctx.toast('Этот фрагмент уже изменён или удалён', 'warn');
}

